import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { subscriptionAPI } from 'redux/subscriptionAPI';
import { invoiceAPI } from 'redux/invoiceAPI';
import { PayerError } from '@payer-admin-ui/components';
import { patch } from '@payer-admin-ui/components-v2/src/functions/lodashExtensions';

export const fetchSubscription = createAsyncThunk('subscriptions/fetchById', async ({ subscriptionId }) => {
  return subscriptionAPI.fetchById({ subscriptionId });
});

export const updateSubscription = createAsyncThunk('subscriptions/updateSubscription', async (_, { getState }) => {
  const {
    subscription: {
      id,
      externalReferenceId,
      periodTime,
      periodTimeUnit,
      endDate,
      deliveryType,
      subscriptionPaymentMethod,
      invoiceConfig,
      templateOrder: { additionalReferenceId, description, comment, invoiceCustomer, items, referenceId },
    },
  } = getState().subscriptions.updateSubscription;

  const subscription = {
    subscriptionId: id,
    orderData: {
      referenceId,
      additionalReferenceId,
      description,
      comment,
      items,
      invoiceCustomer,
      deliveryCustomer: null,
      orderingCustomer: null,
      sellingCustomer: null,
    },
  };
  const cardSubscriptionConfig = {
    externalReferenceId,
    deliveryType,
  };
  const invoiceSubscriptionConfig = {
    externalReferenceId,
    deliveryType,
    periodTimeUnit,
    periodTime,
    endDate,
  };
  if (subscriptionPaymentMethod === 'CARD') {
    subscription['subscriptionConfig'] = cardSubscriptionConfig;
    return subscriptionAPI.updateOrderAndCardSubscription(subscription);
  }
  if (subscriptionPaymentMethod === 'INVOICE') {
    subscription['subscriptionConfig'] = invoiceSubscriptionConfig;
    subscription['invoiceConfig'] = invoiceConfig;
    return subscriptionAPI.updateOrderAndInvoiceSubscription(subscription);
  }
  throw new PayerError({ code: 'subscriptionPaymentMethod' });
});

export const getAddress = createAsyncThunk('subscriptions/getAddress', async (_, { getState }) => {
  const {
    regNumber,
    address: { countryCode },
  } = getState().subscriptions.updateSubscription.subscription.templateOrder.invoiceCustomer;
  return invoiceAPI.getAddress({
    countryCode,
    regNumber,
  });
});

const initialState = {
  subscription: null,
  initialSubscription: null,
  fetchSubscription: { isFetching: false, success: false },
  addressFetchState: { isFetching: false, success: false },
  submit: { isFetching: false, success: false },
  error: null,
};

const updateSubscriptionSlice = createSlice({
  name: 'subscriptions/update',
  initialState,
  reducers: {
    resetError: state => ({ ...state, error: null }),
    reset: _state => ({ ...initialState }),
    patchSubscription: (state, { payload: subscriptionPatch }) => {
      patch(state.subscription, subscriptionPatch);
    },
    resetGetAddressSuccess: state => {
      state.addressFetchState.success = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchSubscription.pending, (state, _) => ({
      ...state,
      fetchSubscription: { isFetching: true },
      error: null,
    }));
    builder.addCase(fetchSubscription.fulfilled, (state, { payload: subscription }) => ({
      ...state,
      subscription,
      initialSubscription: subscription,
      fetchSubscription: { isFetching: false, success: true },
      error: null,
    }));
    builder.addCase(fetchSubscription.rejected, (state, { error }) => ({
      ...state,
      subscription: undefined,
      fetchSubscription: { isFetching: false },
      error,
    }));

    builder.addCase(updateSubscription.pending, (state, _) => ({
      ...state,
      submit: { isFetching: true, success: false },
      error: null,
    }));
    builder.addCase(updateSubscription.fulfilled, (state, _) => ({
      ...state,
      submit: {
        isFetching: false,
        success: true,
      },
    }));
    builder.addCase(updateSubscription.rejected, (state, { error }) => ({
      ...state,
      submit: { isFetching: false },
      error,
    }));

    builder.addCase(getAddress.pending, (state, _) => ({
      ...state,
      addressFetchState: { isFetching: true, success: false },
      error: null,
    }));
    builder.addCase(
      getAddress.fulfilled,
      (
        state,
        {
          payload: {
            companyName,
            yourReference,
            firstName,
            lastName,
            streetAddress1,
            streetAddress2,
            zipCode,
            city,
            countryCode,
          },
        },
      ) =>
        patch(state, {
          subscription: {
            templateOrder: {
              invoiceCustomer: {
                address: {
                  companyName: companyName || null,
                  yourReference: yourReference || null,
                  firstName: firstName || null,
                  lastName: lastName || null,
                  streetAddress1: streetAddress1 || null,
                  streetAddress2: streetAddress2 || null,
                  zipCode: zipCode || null,
                  city: city || null,
                  countryCode: countryCode || null,
                },
              },
            },
          },
          addressFetchState: { isFetching: false, success: true },
        }),
    );
    builder.addCase(getAddress.rejected, (state, { error }) => ({
      ...state,
      addressFetchState: { isFetching: false },
      error,
    }));
  },
});

export const {
  actions: { resetError, reset, resetGetAddressSuccess, patchSubscription },
  reducer: updateSubscriptionReducer,
} = updateSubscriptionSlice;
